import { Box, Popover, Stack, Text, useMantineTheme } from '@mantine/core';
import { useState } from 'react';

export interface ProgressSegment {
  value: number;
  color: string;
  label: string;
  qoqPct?: number | null;
}

interface StackedProgressBarProps {
  segments: ProgressSegment[];
  threshold?: number;
  thresholdLabel?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function StackedProgressBar({
  segments,
  threshold,
  thresholdLabel,
  size = 'lg',
}: StackedProgressBarProps) {
  const theme = useMantineTheme();
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null);
  const [hoveredThreshold, setHoveredThreshold] = useState(false);

  const total = segments.reduce((sum, seg) => sum + seg.value, 0);
  const cumulative: number[] = [];
  let currentSum = 0;

  segments.forEach((seg) => {
    cumulative.push(currentSum);
    currentSum += seg.value;
  });

  // Match Mantine Progress feel: pill shape (radius = height/2)
  const height = size === 'sm' ? 10 : size === 'md' ? 12 : 16;
  const radiusValue = Math.round(height / 2);

  const trackBg = theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1];
  const trackBorder = theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3];
  const markerColor = theme.colorScheme === 'dark' ? theme.white : theme.colors.dark[9];
  const dividerColor =
    theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.35)' : 'rgba(255, 255, 255, 0.65)';

  const tooltipStyles = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    padding: '10px 12px',
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    maxWidth: 260,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    lineHeight: 1.25,
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', overflow: 'visible' }}>
      <Box
        sx={{
          height,
          position: 'relative',
          backgroundColor: trackBg,
          boxShadow: `inset 0 0 0 1px ${trackBorder}`,
          borderRadius: radiusValue,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {segments.map((seg, segIndex) => {
          const isZero = seg.value <= 0 || total <= 0;
          if (isZero) return null;

          const widthPct = total > 0 ? (seg.value / total) * 100 : 0;
          const leftPct = total > 0 ? (cumulative[segIndex] / total) * 100 : 0;
          const pct = total > 0 ? Math.round((seg.value / total) * 100) : 0;
          const qoqPct = seg.qoqPct ?? null;
          const qoqArrow = qoqPct && qoqPct !== 0 ? (qoqPct > 0 ? '↑' : '↓') : '';
          const qoqLabel =
            qoqPct === null
              ? null
              : `${qoqArrow}${Math.abs(Math.round(qoqPct))}% QoQ`;
          const isFirst = segIndex === 0;
          const isLast = segIndex === segments.length - 1;

          // Avoid tiny rounding gaps by letting the last segment fill to the end.
          const widthCss = isLast ? `calc(100% - ${leftPct}%)` : `${widthPct}%`;

          return (
            <Popover
              key={segIndex}
              position="top"
              withArrow
              withinPortal
              opened={hoveredSegmentIndex === segIndex}
            >
              <Popover.Target>
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${leftPct}%`,
                    width: widthCss,
                    top: 0,
                    bottom: 0,
                    backgroundColor: seg.color,
                    cursor: 'pointer',
                    transition: 'filter 120ms ease',
                    // Hairline divider (no gap) to avoid overlap look.
                    borderLeft: !isFirst ? `1px solid ${dividerColor}` : 'none',
                    // Let the track clipping create the outer rounding; keep inner edges crisp.
                    borderTopLeftRadius: isFirst ? radiusValue : 0,
                    borderBottomLeftRadius: isFirst ? radiusValue : 0,
                    borderTopRightRadius: isLast ? radiusValue : 0,
                    borderBottomRightRadius: isLast ? radiusValue : 0,
                    '&:hover': {
                      filter: theme.colorScheme === 'dark' ? 'brightness(1.05)' : 'brightness(0.98)',
                    },
                  }}
                  onMouseEnter={() => setHoveredSegmentIndex(segIndex)}
                  onMouseLeave={() => setHoveredSegmentIndex(null)}
                />
              </Popover.Target>
              <Popover.Dropdown
                sx={{
                  ...tooltipStyles,
                  pointerEvents: 'none',
                }}
              >
                <Stack spacing={4}>
                  <Text size="xs" weight={700} sx={{ opacity: 0.95 }}>
                    {seg.label}
                  </Text>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        backgroundColor: seg.color,
                        borderRadius: 2,
                        flex: '0 0 auto',
                      }}
                    />
                    <Text size="sm" weight={700}>
                      {seg.value}
                    </Text>
                    <Text size="sm" sx={{ opacity: 0.8 }}>
                      ({pct}%)
                    </Text>
                  </Box>
                  {qoqLabel && (
                    <Text size="xs" sx={{ opacity: 0.75 }}>
                      {qoqLabel}
                    </Text>
                  )}
                </Stack>
              </Popover.Dropdown>
            </Popover>
          );
        })}
        {threshold !== undefined && (
          <Popover position="top" withArrow withinPortal opened={hoveredThreshold}>
            <Popover.Target>
              <Box
                sx={{
                  position: 'absolute',
                  left: `${threshold}%`,
                  top: 0,
                  bottom: 0,
                  width: 14, // easy hover area
                  backgroundColor: 'transparent',
                  pointerEvents: 'auto',
                  zIndex: 10,
                  transform: 'translateX(-50%)',
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setHoveredThreshold(true)}
                onMouseLeave={() => setHoveredThreshold(false)}
              />
            </Popover.Target>
            <Popover.Dropdown
              sx={{
                ...tooltipStyles,
                pointerEvents: 'none',
              }}
            >
              <Text size="xs" weight={700}>
                {thresholdLabel || `Threshold: ${threshold}%`}
              </Text>
            </Popover.Dropdown>
          </Popover>
        )}
      </Box>
      {threshold !== undefined && (
        <Box
          sx={{
            position: 'absolute',
            left: `${threshold}%`,
            top: height + 2,
            width: 0,
            height: 0,
            transform: 'translateX(-50%)',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: `6px solid ${markerColor}`,
            opacity: hoveredThreshold ? 0.9 : 0.6,
            transition: 'opacity 120ms ease',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      )}
    </Box>
  );
}
